import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { DataGrid, GridActionsCellItem, gridClasses, GridToolbar } from "@mui/x-data-grid";
import { Box, Chip, LinearProgress, useTheme, styled, Tooltip, TextField, MenuItem, Stack, InputAdornment } from "@mui/material";
import { frFR } from '@mui/x-data-grid/locales';
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import agentService from "../services/agentService";
import ProfilPersonnelModal from "./modals/ProfilePersonnelModal";
import RegisterPersonnel from "./modals/RegisterPersonnelModal";
import DeleteDialog from "./modals/DeleteDialog";
import EditPersonnelModal from "./modals/EditPersonnelModal";
import ToggleStatusDialog from "./modals/ToggleStatusDialog";
import useAuthStore from "../store/authStore";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${gridClasses.root}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  [`& .${gridClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
  },
}));

const DataGridPersonnel = () => {
  const user = useAuthStore((state) => state.user);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isVisibleProfilPersonnel, setIsVisibleProfilPersonnel] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isToggleStatusDialogOpen, setIsToggleStatusDialogOpen] = useState(false);
  const [rows, setRows] = useState([]);

  // Filtres rapides
  const [filterNom, setFilterNom] = useState('');
  const [filterMatricule, setFilterMatricule] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  const getAgents = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await agentService.getAllAgents(1, 200, '', user.partie_id, user.role);
      const data = response?.data?.map((row, index) => ({
        ...row,
        id: row.id || index,
        order: index + 1,
        nom: row.nom || "N/A",
        prenom: row.prenom || "N/A",
        matricule: row.matricule || "N/A",
        genre: row.genre || "N/A",
        tel: row.tel || "N/A",
        email: row.email || "N/A",
        role: row.role || "N/A",
        statut: row.statut !== undefined ? row.statut : false,
      })) || [];
      setRows(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des agents:", error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [user.partie_id, user.role]);

  useEffect(() => {
    getAgents();
  }, [getAgents]);

  const handleRefresh = useCallback(() => {
    getAgents();
  }, [getAgents]);

  const handleAction = useCallback((row, actionType) => {
    if (!row) return;
    setSelectedRow(row);
    switch (actionType) {
      case "seedetail":
        setIsVisibleProfilPersonnel(true);
        break;
      case "delete":
        setIsDeleteDialogOpen(true);
        break;
      case "edit":
        setIsEditModalOpen(true);
        break;
      default:
        break;
    }
  }, []);

  const handleToggleStatus = useCallback(async () => {
    try {
      if (!selectedRow?.id) {
        toast.error("Impossible de modifier le statut : ID non trouvé");
        return;
      }
      await agentService.toggleAgentStatus(selectedRow.id);
      toast.success(`Le statut de l'agent a été ${selectedRow.statut ? 'désactivé' : 'activé'} avec succès`);
      handleRefresh();
      setIsToggleStatusDialogOpen(false);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Une erreur est survenue lors du changement de statut";
      toast.error(errorMessage);
      console.error("Erreur détaillée:", error);
    }
  }, [selectedRow, handleRefresh]);

  // Filtres rapides sur les données
  const filteredRows = useMemo(() => {
    return rows.filter(row =>
      (!filterNom || row.nom.toLowerCase().includes(filterNom.toLowerCase())) &&
      (!filterMatricule || row.matricule.toLowerCase().includes(filterMatricule.toLowerCase())) &&
      (!filterRole || row.role === filterRole) &&
      (!filterStatut || (filterStatut === 'actif' ? row.statut : !row.statut))
    );
  }, [rows, filterNom, filterMatricule, filterRole, filterStatut]);

  // Mapping des rôles par cluster
  const roleMapping = {
    agent_assurance: 'Agent Assurance',
    admin_clinique: 'Admin',
    agent_clinique: 'Agent',
    medecin_clinique: 'Médecin',
    admin_pharmacie: 'Admin Pharmacie',
    agent_pharmacie: 'Agent Pharmacie',
    admin_laboratoire: 'Admin Laboratoire',
    agent_laboratoire: 'Agent Laboratoire',
    admin_clinique_laboratoire: 'Admin Clinique/Labo',
    agent_clinique_laboratoire: 'Agent Clinique/Labo',
    medecin_clinique_laboratoire: 'Médecin Clinique/Labo',
  };

  // Définir les rôles filtrables selon le cluster de l'utilisateur connecté
  const getRoleOptionsByUser = () => {
    if (!user?.role) return [];
    if (user.role.includes('clinique')) {
      return [
        { value: 'admin_clinique', label: roleMapping['admin_clinique'] },
        { value: 'agent_clinique', label: roleMapping['agent_clinique'] },
        { value: 'medecin_clinique', label: roleMapping['medecin_clinique'] },
      ];
    }
    if (user.role.includes('pharmacie')) {
      return [
        { value: 'admin_pharmacie', label: roleMapping['admin_pharmacie'] },
        { value: 'agent_pharmacie', label: roleMapping['agent_pharmacie'] },
      ];
    }
    if (user.role.includes('laboratoire')) {
      return [
        { value: 'admin_laboratoire', label: roleMapping['admin_laboratoire'] },
        { value: 'agent_laboratoire', label: roleMapping['agent_laboratoire'] },
      ];
    }
    if (user.role.includes('assurance')) {
      return [
        { value: 'agent_assurance', label: roleMapping['agent_assurance'] },
      ];
    }
    // Par défaut, tous les rôles
    return Object.entries(roleMapping).map(([value, label]) => ({ value, label }));
  };

  const roleOptions = getRoleOptionsByUser();

  const columns = useMemo(() => [
    {
      field: "order",
      headerName: "N°",
      width: 70,
      headerClassName: 'super-app-theme--header',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "matricule",
      headerName: "Matricule",
      width: 120,
      headerClassName: 'super-app-theme--header',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "nom",
      headerName: "Nom",
      flex: 1,
      minWidth: 120,
      headerClassName: 'super-app-theme--header',
      renderCell: (params) => (
        <b style={{ color: theme.palette.primary.dark }}>{params.value}</b>
      ),
    },
    {
      field: "prenom",
      headerName: "Prénom",
      flex: 1,
      minWidth: 120,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: "genre",
      headerName: "Genre",
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.value === "homme") {
          return <Chip icon={<CheckCircleOutlineIcon color="success" />} label="Homme" color="success" size="small" />;
        }
        if (params.value === "femme") {
          return <Chip icon={<CheckCircleOutlineIcon color="info" />} label="Femme" color="info" size="small" />;
        }
        return <Chip label="N/A" size="small" />;
      },
      headerClassName: 'super-app-theme--header',
    },
    {
      field: "tel",
      headerName: "Téléphone",
      width: 150,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: "role",
      headerName: "Rôle",
      width: 200,
      renderCell: (params) => {
        const role = params.value;
        const displayRole = roleMapping[role] || role;
        return (
          <Chip
            label={displayRole}
            color="default"
            variant="outlined"
            size="small"
            sx={{ fontWeight: 600, fontSize: 13 }}
          />
        );
      },
      headerClassName: 'super-app-theme--header',
    },
    {
      field: "statut",
      headerName: "Statut",
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Tooltip title={params.value ? "Actif" : "Inactif"}>
          {params.value ? (
            <Chip icon={<CheckCircleOutlineIcon />} label="Actif" color="success" size="small" />
          ) : (
            <Chip icon={<HighlightOffIcon />} label="Inactif" color="error" size="small" />
          )}
        </Tooltip>
      ),
      headerClassName: 'super-app-theme--header',
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityOutlinedIcon color="info" />}
          label="Voir"
          key="seedetail"
          onClick={() => handleAction(params.row, "seedetail")}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<EditOutlinedIcon color="warning" />}
          label="Modifier"
          key="edit"
          onClick={() => handleAction(params.row, "edit")}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<DeleteOutlineOutlinedIcon color="error" />}
          label="Supprimer"
          key="delete"
          onClick={() => handleAction(params.row, "delete")}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<PowerSettingsNewIcon color={params.row.statut ? "error" : "success"} />}
          label={params.row.statut ? "Désactiver" : "Activer"}
          key="toggle-status"
          onClick={() => {
            setSelectedRow(params.row);
            setIsToggleStatusDialogOpen(true);
          }}
          showInMenu
        />,
      ],
      headerClassName: 'super-app-theme--header',
    },
  ], [handleAction, theme.palette.primary.dark]);

  // Toolbar personnalisé avec export CSV et filtres rapides
  const CustomToolbar = () => (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="flex-start">
        <TextField
          label="Nom"
          value={filterNom}
          onChange={e => setFilterNom(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="Matricule"
          value={filterMatricule}
          onChange={e => setFilterMatricule(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 140 }}
        />
        <TextField
          select
          label="Rôle"
          value={filterRole}
          onChange={e => setFilterRole(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Tous</MenuItem>
          {roleOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Statut"
          value={filterStatut}
          onChange={e => setFilterStatut(e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">Tous</MenuItem>
          <MenuItem value="actif">Actif</MenuItem>
          <MenuItem value="inactif">Inactif</MenuItem>
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <RegisterPersonnel onRefresh={handleRefresh} variant="contained" />
      </Stack>
      <GridToolbar
        csvOptions={{
          fileName: 'agents_export',
          utf8WithBom: true,
        }}
        printOptions={{ disableToolbarButton: true }}
        showQuickFilter={false}
      />
    </Box>
  );

  return (
    <Box
      component="section"
      sx={{
        height: 650,
        width: '100%',
        bgcolor: 'background.default',
        '& .super-app-theme--header': {
          background: theme.palette.primary.main,
          color: theme.palette.common.white,
          fontWeight: 'bold',
          fontSize: 16,
        },
        '& .MuiDataGrid-cell': {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '& .MuiDataGrid-row:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '& .MuiDataGrid-footerContainer': {
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        },
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <ProfilPersonnelModal
        open={isVisibleProfilPersonnel}
        onClose={() => setIsVisibleProfilPersonnel(false)}
        user={selectedRow}
      />
      <DeleteDialog
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        handleDeleteConfirm={async () => {
          if (selectedRow) {
            await agentService.deleteAgent(selectedRow.id);
            handleRefresh();
          }
        }}
        name="l'agent"
      />
      <EditPersonnelModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={selectedRow}
        onRefresh={handleRefresh}
      />
      <ToggleStatusDialog
        open={isToggleStatusDialogOpen}
        onClose={() => setIsToggleStatusDialogOpen(false)}
        onConfirm={handleToggleStatus}
        status={selectedRow?.statut}
        name="l'agent"
      />
      <DataGrid
        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
        slots={{
          toolbar: CustomToolbar,
          loadingOverlay: StyledLinearProgress,
        }}
        columns={columns}
        rows={filteredRows}
        checkboxSelection
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        loading={isLoading}
        sx={{
          borderRadius: 2,
          boxShadow: 2,
          '& .MuiDataGrid-columnHeaders': {
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          },
        }}
        slotProps={{
          pagination: {
            labelRowsPerPage: 'Lignes par page:',
          },
          toolbar: {
            printOptions: { disableToolbarButton: true },
          },
        }}
      />
    </Box>
  );
};

export default DataGridPersonnel;